import { useSelector } from 'src/utils/store'
import * as S from './TableListSwapsOrReturns.styles'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { useMemo, useState } from 'react'
import TableMui from 'src/components/TableMui'
import { EditOutlined, DoubleArrowOutlined, DeleteOutline } from '@mui/icons-material'
import { useHistory } from 'react-router-dom'
import { SwapOrReturnInvoice, SwapOrReturnStatusEnum } from '../../SwapOrReturnTypes'
import { toast } from 'react-toastify'
import { capitalizeString } from 'src/utils/capitalize'
import { IconButton, Tooltip } from '@mui/material'
import { useDispatch } from 'react-redux'
import { actions } from '../../SwapOrReturnState'

export const TableListSwapsOrReturns = () => {
  const history = useHistory()
  const dispatch = useDispatch()

  const [openModalCancel, setOpenModalCancel] = useState(false)
  const [swapOrReturnToCancel, setSwapOrReturnToCancel] = useState<SwapOrReturnInvoice | undefined>(undefined)

  const {
    swapOrReturnInvoices, swapOrReturnToResolveIds, swapOrReturnSearchData, pagination,
  } = useSelector((state) => state.swapOrReturn)

  const onClickEditSwapOrReturn = (id: number) => {
    history.push(`/swap-or-return/create/${id}`)
  }

  const onClickResolveSwapOrReturn = (id: number) => {
    dispatch(actions.setProperty({ property: 'swapOrReturnToResolveIds', value: [id] }))
    history.push('/swap-or-return/resolve')
  }

  const onClickCancelSwapOrReturn = (id: number) => {
    const swapOrReturn = swapOrReturnInvoices.find((swapOrReturnInvoice) => swapOrReturnInvoice.id === id)

    if (!swapOrReturn) {
      toast.error('Não foi possível encontrar a troca ou devolução')
      return
    }

    if (swapOrReturn.status === SwapOrReturnStatusEnum.CANCELED) {
      return
    }

    setSwapOrReturnToCancel(swapOrReturn)
    setOpenModalCancel(true)
  }

  const closeCancelModal = () => {
    setSwapOrReturnToCancel(undefined)
    setOpenModalCancel(false)
  }

  const cancelSwapOrReturn = () => {
    if (!swapOrReturnToCancel) return

    const onSuccess = () => {
      toast.success(`Nota de ${swapOrReturnToCancel.type.toUpperCase()} cancelada com sucesso!`)
      closeCancelModal()
    }

    dispatch(actions.cancelSwapOrReturn({
      id: swapOrReturnToCancel.id,
      onSuccess,
    }))
  }

  const allowedStatusToResolve: string[] = [
    SwapOrReturnStatusEnum.CREATED,
    SwapOrReturnStatusEnum.PENDING,
    SwapOrReturnStatusEnum.IN_RESOLUTION,
    SwapOrReturnStatusEnum.WAITING_RELEASE,
  ]

  const resolveTableRows = useMemo(() => swapOrReturnInvoices.map((swapOrReturn) => ({
    id: swapOrReturn.id,
    supplierName: capitalizeString(swapOrReturn.supplierName),
    invoiceNumber: swapOrReturn.invoiceNumber,
    invoiceAccessKey: swapOrReturn.invoiceAccessKey,
    volume: `${swapOrReturn.volume}L`,
    type: capitalizeString(swapOrReturn.type),
    status: capitalizeString(swapOrReturn.status),
    createdBy: capitalizeString(swapOrReturn.createdBy),
    createdAt: swapOrReturn.createdAt,
    edit: (
      <EditOutlined
        sx={{ cursor: 'pointer' }}
        onClick={() => onClickEditSwapOrReturn(swapOrReturn.id)}
        data-testid='edit-swap-or-return-button-test-id'
      />
    ),
    resolve: (
      <Tooltip
        title={`Não é possível resolver uma ${swapOrReturn.type.toLowerCase()} com status ${swapOrReturn.status.toLowerCase()}.`}
        placement='top'
        disableHoverListener={allowedStatusToResolve.includes(swapOrReturn.status)}
        arrow
      >
        <div>
          <IconButton
            sx={{
              color: 'green', cursor: 'pointer', padding: 0, margin: 0,
            }}
            onClick={() => onClickResolveSwapOrReturn(swapOrReturn.id)}
            data-testid='resolve-swap-or-return-button-test-id'
            disabled={!allowedStatusToResolve.includes(swapOrReturn.status)}
          >
            <DoubleArrowOutlined />
          </IconButton>
        </div>
      </Tooltip>
    ),
    cancel: (
      <Tooltip
        title={`Não é possível cancelar uma ${swapOrReturn.type.toLowerCase()} já cancelada.`}
        placement='top'
        disableHoverListener={swapOrReturn.status !== SwapOrReturnStatusEnum.CANCELED}
        arrow
      >
        <div>
          <IconButton
            sx={{
              color: 'red', cursor: 'pointer', padding: 0, margin: 0,
            }}
            onClick={() => onClickCancelSwapOrReturn(swapOrReturn.id)}
            data-testid='cancel-swap-or-return-button-test-id'
            disabled={swapOrReturn.status === SwapOrReturnStatusEnum.CANCELED}
          >
            <DeleteOutline />
          </IconButton>
        </div>
      </Tooltip>
    ),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [swapOrReturnInvoices])

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selectedIds = resolveTableRows.map((row) => row.id)
      dispatch(actions.setProperty({ property: 'swapOrReturnToResolveIds', value: selectedIds }))
      return
    }
    dispatch(actions.setProperty({ property: 'swapOrReturnToResolveIds', value: [] }))
  }

  const handleCheckboxClick = (id: number) => {
    const selectedIndex = swapOrReturnToResolveIds.findIndex((swapOrReturnToResolveId) => swapOrReturnToResolveId === id)

    if (selectedIndex === -1) {
      const swapOrReturn = swapOrReturnInvoices.find((swapOrReturnInvoice) => swapOrReturnInvoice.id === id)

      if (swapOrReturn) {
        dispatch(actions.setProperty({ property: 'swapOrReturnToResolveIds', value: [...swapOrReturnToResolveIds, swapOrReturn.id] }))
      }
    } else {
      const updatedSelectedIds = swapOrReturnToResolveIds.filter((swapOrReturnToResolveId) => swapOrReturnToResolveId !== id)
      dispatch(actions.setProperty({ property: 'swapOrReturnToResolveIds', value: updatedSelectedIds }))
    }
  }

  const getSwapsOrReturns = (page: number, perPage: number, order?: string, orderBy?: string | number) => {
    if (!swapOrReturnSearchData) return

    dispatch(actions.getSwapsOrReturns({
      ...swapOrReturnSearchData,
      page,
      perPage,
      order,
      orderBy,
    }))
  }

  return (
    <>
      <TableMui
        columns={[
          {
            id: 'supplierName', label: 'Fornecedor', testId: 'supplier-name-column-test-id', limitTextAndShowInTooltip: 20,
          },
          { id: 'invoiceNumber', label: 'Nº da Nota', testId: 'invoice-number-column-test-id' },
          {
            id: 'invoiceAccessKey', label: 'Chave da Nota', testId: 'invoice-access-key-column-test-id', limitTextAndShowInTooltip: 15,
          },
          { id: 'volume', label: 'Volume', testId: 'volume-column-test-id' },
          { id: 'type', label: 'Tipo', testId: 'type-column-test-id' },
          { id: 'status', label: 'Status', testId: 'status-column-test-id' },
          { id: 'createdBy', label: 'Criado por', testId: 'created-by-column-test-id' },
          {
            id: 'createdAt', label: 'Data de criação', testId: 'created-at-column-test-id', isDate: true,
          },
          {
            id: 'edit', label: 'Editar', testId: 'edit-column-test-id', useSorting: false,
          },
          {
            id: 'resolve', label: 'Resolver', testId: 'resolve-column-test-id', useSorting: false,
          },
          {
            id: 'cancel', label: 'Cancelar', testId: 'cancel-column-test-id', useSorting: false,
          },
        ]}
        emptyLabel='Utilize o filtro para buscar as trocas e devoluções'
        rows={resolveTableRows}
        rowCount={resolveTableRows.length}
        enablePagination
        rowsPerPageOptions={[5, 10, 25, 30, 50, 100, 200]}
        minHeight='54vh'
        maxHeight='54vh'
        hasSorting
        hasCheckbox
        selected={swapOrReturnToResolveIds}
        selectedCount={swapOrReturnToResolveIds.length}
        onRowCheckboxClick={handleCheckboxClick}
        onSelectAllClick={handleSelectAllClick}
        getPaginatedItems={getSwapsOrReturns}
        rowsPerPageLimit={pagination.perPage}
        totalPages={pagination.totalPages}
        currentPage={pagination.currentPage - 1}
        totalItems={pagination.totalItems}
        testId='swap-or-return-table-test'
      />
      {
        swapOrReturnToCancel
        && (
          <ConfirmOrRejectModal
            show={openModalCancel}
            title={`Cancelar ${swapOrReturnToCancel.type.toUpperCase()}`}
            customizableDescription={(
              <S.ContainerDescription>
                <S.ContainerInfo>
                  <S.Text>Fornecedor:</S.Text>
                  <S.TextDescription> {capitalizeString(swapOrReturnToCancel.supplierName)}</S.TextDescription>
                </S.ContainerInfo>
                <S.ContainerInfo>
                  <S.Text>Nº da Nota:</S.Text>
                  <S.TextDescription> {swapOrReturnToCancel.invoiceNumber}</S.TextDescription>
                </S.ContainerInfo>
                <S.ContainerInfo style={{ marginTop: '.8rem' }}>
                  <S.TextDescription>Deseja realmente cancelar essa nota de {swapOrReturnToCancel.type.toUpperCase()}?</S.TextDescription>
                </S.ContainerInfo>
              </S.ContainerDescription>
            )}
            onConfirm={() => cancelSwapOrReturn()}
            onReject={(closeCancelModal)}
            onClose={closeCancelModal}
            dataTestId='cancel-swap-or-return-modal-test-id'
          />
        )
      }
    </>
  )
}
